// Admin reconciliation view for payments.
// Reuses the same layout/styling pattern as AdminUsersPage.
import React, { useState, useEffect, useCallback } from 'react';
import { Search, RefreshCcw, X, AlertTriangle, FileText, Loader2 } from 'lucide-react';
import AppHeader from '../components/AppHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUSES = [
  { v: '', label: 'All statuses' },
  { v: 'pending', label: 'Pending' },
  { v: 'confirmed', label: 'Confirmed' },
  { v: 'failed', label: 'Failed' },
  { v: 'refunded', label: 'Refunded' },
  { v: 'partially_refunded', label: 'Partially refunded' },
  { v: 'verification_failed', label: 'Verification failed' },
];

const GATEWAYS = [
  { v: '', label: 'All gateways' },
  { v: 'razorpay', label: 'Razorpay' },
  { v: 'zoho', label: 'Zoho' },
];

const PURPOSES = [
  { v: '', label: 'All purposes' },
  { v: 'booking', label: 'Booking' },
  { v: 'extension', label: 'Extension' },
  { v: 'other', label: 'Other' },
];

const STATUS_COLORS = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
  refunded: 'bg-purple-50 text-purple-700 border-purple-200',
  partially_refunded: 'bg-purple-50 text-purple-700 border-purple-200',
  verification_failed: 'bg-red-50 text-red-700 border-red-200',
};

export default function AdminPaymentsPage() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total: 0, by_status: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({
    status: '', gateway: '', purpose: '', date_from: '', date_to: '', q: '',
  });

  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [noteText, setNoteText] = useState('');

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params.append(k, v);
      });
      const res = await fetch(`${API_URL}/api/payments/admin?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setItems(data.items || []);
      setSummary(data.summary || { total: 0, by_status: {} });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [filters, token]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const openDetail = async (paymentId) => {
    setDetailLoading(true);
    setSelected({ id: paymentId });
    try {
      const res = await fetch(`${API_URL}/api/payments/admin/${paymentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSelected(data);
    } catch (e) {
      setError(e.message);
      setSelected(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const addNote = async () => {
    if (!noteText.trim() || !selected) return;
    const res = await fetch(
      `${API_URL}/api/payments/admin/${selected.id}/note?note=${encodeURIComponent(noteText.trim())}`,
      { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
    );
    if (res.ok) {
      setNoteText('');
      await openDetail(selected.id);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="admin-payments-page">
      <AppHeader
        title="Payments & Reconciliation"
        rightSlot={
          <button
            onClick={fetchPayments}
            className="inline-flex items-center gap-1 text-sm px-3 min-h-[44px] rounded-full hover:bg-white/10"
            data-testid="payments-refresh-btn"
          >
            <RefreshCcw size={16} /> <span className="hidden sm:inline">Refresh</span>
          </button>
        }
      />

      <div className="container mx-auto px-6 py-8">
        {/* Summary chips */}
        <div className="flex flex-wrap gap-2 mb-5" data-testid="payments-summary">
          <span className="px-3 py-1 text-xs bg-white border border-gray-200 rounded-full">
            Total: <strong>{summary.total}</strong>
          </span>
          {Object.entries(summary.by_status).map(([s, n]) => (
            <span key={s} className={`px-3 py-1 text-xs rounded-full border ${STATUS_COLORS[s] || 'bg-gray-50'}`}>
              {s}: <strong>{n}</strong>
            </span>
          ))}
        </div>

        {/* Filters */}
        <div className="card p-4 mb-5 grid md:grid-cols-6 gap-3" data-testid="payments-filters">
          <div className="md:col-span-2 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search seeker / masked ID / booking ID / gateway ref…"
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg"
              data-testid="payments-search"
            />
          </div>
          <select
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
            data-testid="payments-filter-status"
          >
            {STATUSES.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
          <select
            value={filters.gateway}
            onChange={(e) => setFilters((f) => ({ ...f, gateway: e.target.value }))}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
            data-testid="payments-filter-gateway"
          >
            {GATEWAYS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
          <select
            value={filters.purpose}
            onChange={(e) => setFilters((f) => ({ ...f, purpose: e.target.value }))}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
            data-testid="payments-filter-purpose"
          >
            {PURPOSES.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
          <input
            type="date"
            value={filters.date_from}
            onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
            data-testid="payments-filter-date-from"
          />
          <input
            type="date"
            value={filters.date_to}
            onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
            data-testid="payments-filter-date-to"
          />
        </div>

        {error && (
          <div className="card p-4 mb-5 bg-red-50 border-red-200 text-sm text-red-700 flex items-center gap-2">
            <AlertTriangle size={16} /> {error}
          </div>
        )}

        {/* Table */}
        <div className="card overflow-x-auto">
          {loading ? (
            <div className="p-12 flex items-center justify-center">
              <Loader2 className="animate-spin text-primary" size={28} />
            </div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-sm text-[#718096]">No payments match the current filters.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#FAF9F6] text-[#718096]">
                <tr>
                  <th className="text-left p-3 font-medium">Date</th>
                  <th className="text-left p-3 font-medium">Seeker</th>
                  <th className="text-left p-3 font-medium">Masked ID</th>
                  <th className="text-left p-3 font-medium">Purpose</th>
                  <th className="text-right p-3 font-medium">Amount</th>
                  <th className="text-left p-3 font-medium">Gateway</th>
                  <th className="text-left p-3 font-medium">Status</th>
                  <th className="text-left p-3 font-medium">Refund</th>
                  <th className="text-left p-3 font-medium">Gateway ref</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`payment-row-${p.id}`}>
                    <td className="p-3 whitespace-nowrap text-[#4A5568]">
                      {new Date(p.created_at).toLocaleString()}
                    </td>
                    <td className="p-3 text-[#2D3748]">{p.seeker_name || '—'}</td>
                    <td className="p-3 font-mono text-xs text-[#4A5568]">{p.masked_id || '—'}</td>
                    <td className="p-3 capitalize">{p.purpose}</td>
                    <td className="p-3 text-right font-medium">
                      ₹{p.amount_inr}
                      {p.amount_mismatch && (
                        <span className="ml-1 text-xs text-amber-600" title="Expected vs confirmed mismatch">⚠</span>
                      )}
                    </td>
                    <td className="p-3 capitalize">{p.gateway}</td>
                    <td className="p-3">
                      <span className={`inline-block px-2 py-0.5 text-xs rounded-full border ${STATUS_COLORS[p.status] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="p-3 text-[#4A5568]">
                      {p.refund_amount_inr > 0 ? `₹${p.refund_amount_inr}` : '—'}
                    </td>
                    <td className="p-3 font-mono text-[11px] text-[#718096] truncate max-w-[160px]" title={p.gateway_payment_id || p.gateway_order_id}>
                      {p.gateway_payment_id || p.gateway_order_id || '—'}
                    </td>
                    <td className="p-3">
                      <button
                        className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                        onClick={() => openDetail(p.id)}
                        data-testid={`payment-detail-btn-${p.id}`}
                      >
                        <FileText size={14} /> View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" data-testid="payment-detail-drawer">
          <div className="w-full max-w-xl bg-white h-full overflow-y-auto">
            <div className="p-5 border-b flex items-center justify-between sticky top-0 bg-white">
              <h2 className="font-heading text-lg font-semibold">Payment detail</h2>
              <button onClick={() => setSelected(null)} className="text-gray-500 hover:text-gray-800" data-testid="payment-detail-close">
                <X size={22} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {detailLoading ? (
                <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-primary" size={28} /></div>
              ) : (
                <>
                  <DetailField label="Internal payment ID" value={selected.id} mono />
                  <DetailField label="Gateway" value={selected.gateway} />
                  <DetailField label="Purpose" value={selected.purpose} />
                  <DetailField label="Status" value={selected.status} />
                  <DetailField label="Amount" value={selected.amount_inr != null ? `₹${selected.amount_inr}` : '—'} />
                  {selected.amount_mismatch && (
                    <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                      ⚠ Expected ₹{selected.expected_amount_inr} ≠ confirmed ₹{selected.amount_inr}
                    </div>
                  )}
                  <DetailField label="Refund amount" value={selected.refund_amount_inr ? `₹${selected.refund_amount_inr}` : '—'} />
                  <DetailField label="Failure reason" value={selected.failure_reason || '—'} />
                  <hr />
                  <DetailField label="User ID" value={selected.user_id || '—'} mono />
                  <DetailField label="Masked ID" value={selected.masked_id || '—'} mono />
                  <DetailField label="Appointment ID" value={selected.appointment_id || '—'} mono />
                  <DetailField label="Session ID" value={selected.session_id || '—'} mono />
                  <DetailField label="Extension request ID" value={selected.extension_request_id || '—'} mono />
                  <hr />
                  <DetailField label="Gateway order ID" value={selected.gateway_order_id || '—'} mono />
                  <DetailField label="Gateway payment ID" value={selected.gateway_payment_id || '—'} mono />
                  <DetailField label="Gateway refund ID" value={selected.gateway_refund_id || '—'} mono />
                  <hr />

                  <h3 className="font-medium text-sm">Admin notes</h3>
                  <div className="space-y-2">
                    {(selected.admin_notes || []).length === 0 && (
                      <p className="text-xs text-[#718096]">No notes yet.</p>
                    )}
                    {(selected.admin_notes || []).map((n) => (
                      <div key={n.id} className="text-xs bg-[#FAF9F6] rounded p-2">
                        <div className="text-[#4A5568]">{n.note}</div>
                        <div className="text-[#A0AEC0] mt-1">— {new Date(n.at).toLocaleString()}</div>
                      </div>
                    ))}
                    <div className="flex gap-2 pt-2">
                      <input
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        placeholder="Add a reconciliation note…"
                        className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg"
                        data-testid="payment-note-input"
                      />
                      <button onClick={addNote} className="btn-primary text-sm" data-testid="payment-note-add">Add</button>
                    </div>
                  </div>

                  {selected.webhook_events && selected.webhook_events.length > 0 && (
                    <>
                      <hr />
                      <h3 className="font-medium text-sm">Webhook events</h3>
                      {selected.webhook_events.map((e) => (
                        <div key={e.id} className="text-xs bg-[#FAF9F6] rounded p-2">
                          <div><strong>{e.event_type}</strong> → {e.internal_status}</div>
                          <div className="text-[#A0AEC0]">{new Date(e.received_at).toLocaleString()}</div>
                        </div>
                      ))}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailField({ label, value, mono }) {
  return (
    <div className="flex justify-between gap-3 text-sm">
      <div className="text-[#718096]">{label}</div>
      <div className={`text-[#2D3748] text-right break-all ${mono ? 'font-mono text-xs' : ''}`}>{value}</div>
    </div>
  );
}
